import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Box,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Button,
  Select,
  MenuItem,
  TextField,
  Switch,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import TranslateIcon from "@mui/icons-material/Translate";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import LinkIcon from "@mui/icons-material/Link";
import PolicyIcon from "@mui/icons-material/Policy";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BlockIcon from "@mui/icons-material/Block";
import DevicesIcon from "@mui/icons-material/Devices";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import InstagramIcon from "@mui/icons-material/Instagram";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import AvTimerIcon from "@mui/icons-material/AvTimer";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SaveIcon from "@mui/icons-material/Save";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockIcon from "@mui/icons-material/Lock";
import HelpCenterIcon from "@mui/icons-material/HelpCenter";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ContactsIcon from "@mui/icons-material/Contacts";
import EmailIcon from "@mui/icons-material/Email";
import { getBotWhitelist, saveBotWhitelist, getBotLanguage, saveBotLanguage } from "../api/botApi";
import PlanUpgrade from "../components/Common/PlanUpgrade";

const NAV = [
  { id: "language", label: "Language", icon: <TranslateIcon />, color: "#6366f1" },
  { id: "autotrigger", label: "Auto Trigger", icon: <FlashOnIcon />, color: "#f59e0b" },
  { id: "whitelist", label: "White/Blacklisting Urls", icon: <LinkIcon />, color: "#10b981" },
  { id: "consent", label: "Consent", icon: <PolicyIcon />, color: "#8b5cf6" },
  { id: "tts", label: "Text to Speech", icon: <RecordVoiceOverIcon />, color: "#ec4899" },
  { id: "botTiming", label: "Bot Active Timings", icon: <AccessTimeIcon />, color: "#06b6d4" },
  { id: "banUsers", label: "Ban/Unban Users (Live Chat)", icon: <BlockIcon />, color: "#ef4444" },
  { id: "devices", label: "Enabled Devices", icon: <DevicesIcon />, color: "#14b8a6" },
  {
    id: "waIg",
    label: "WhatsApp/Instagram",
    icon: <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}><WhatsAppIcon fontSize="small" /><InstagramIcon fontSize="small" /></Box>,
    color: "#25D366",
  },
  { id: "advanced", label: "Advanced", icon: <SettingsSuggestIcon />, color: "#6366f1" },
  { id: "canned", label: "Canned Responses", icon: <FlashOnIcon />, color: "#10b981" },
  { id: "aiBot", label: "AI Bot", icon: <SmartToyIcon />, color: "#8b5cf6" },
  { id: "helpCenter", label: "Help Center", icon: <HelpCenterIcon />, color: "#0ea5e9" },
  { id: "knowledgeBase", label: "Knowledge Base", icon: <MenuBookIcon />, color: "#0891b2" },
  { id: "contactCollection", label: "Contact Collection", icon: <ContactsIcon />, color: "#ec4899" },
  { id: "emailAutomation", label: "Email Automation", icon: <EmailIcon />, color: "#10b981" },
  { id: "instagram", label: "Instagram DM", icon: <InstagramIcon />, color: "#E4405F" },
  { id: "sla", label: "SLA Time", icon: <AvTimerIcon />, color: "#f97316" },
  { id: "security", label: "Security", icon: <LockIcon />, color: "#ef4444" },
];

const ALLOWED_TABS = new Set(NAV.map((n) => n.id));

function useTabSync(defaultTab = "language") {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const safeTab = useMemo(() => {
    const q = params.get("tab");
    return ALLOWED_TABS.has(q || "") ? q : defaultTab;
  }, [params, defaultTab]);
  const [active, setActive] = useState(safeTab);
  useEffect(() => { setActive(safeTab); }, [safeTab]);
  const setActiveAndUrl = useCallback((id) => {
    if (!ALLOWED_TABS.has(id)) return;
    const p = new URLSearchParams(location.search);
    p.set("tab", id);
    navigate({ pathname: location.pathname, search: `?${p.toString()}` }, { replace: true });
    setActive(id);
  }, [location.pathname, location.search, navigate]);
  return [active, setActiveAndUrl];
}

const PRIMARY = "#4F46E5";
const PRIMARY_DARK = "#4338CA";

function KnowledgeBaseTab() {
  const ACCENT = "#0891b2";
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [expandedDocs, setExpandedDocs] = useState({});
  const [chunksCache, setChunksCache] = useState({});
  const botId = localStorage.getItem("selectedBotId");

  // Crawl state
  const [crawlUrl, setCrawlUrl] = useState("");
  const [crawlMax, setCrawlMax] = useState(50);
  const [crawling, setCrawling] = useState(false);
  const [crawlSession, setCrawlSession] = useState(null);
  const [showCrawledPages, setShowCrawledPages] = useState(false);
  const [crawlPages, setCrawlPages] = useState([]);
  const [whitelist, setWhitelist] = useState([]);

  // Queue state
  const [queueInfo, setQueueInfo] = useState(null);
  const [queued, setQueued] = useState(false);

  // Persisted crawl info
  const [crawlInfo, setCrawlInfo] = useState({ lastUrl: null, lastCrawledAt: null, pageCount: 0 });

  // Dialogs
  const [deleteDialog, setDeleteDialog] = useState({ open: false, docName: "" });
  const [noNewPagesDialog, setNoNewPagesDialog] = useState(false);

  useEffect(() => {
    if (!botId) return;
    import("../api/botApi").then(({ getBotWhitelist, getBotCrawlInfo }) => {
      getBotWhitelist(botId).then(res => setWhitelist(res.whitelist || [])).catch(() => {});
      getBotCrawlInfo(botId).then(res => {
        setCrawlInfo(res);
        if (res.lastUrl) setCrawlUrl(res.lastUrl);
      }).catch(() => {});
    });
  }, [botId]);

  // Derive domain validation
  const crawlDomain = crawlUrl.startsWith("http") ? (() => { try { return new URL(crawlUrl).hostname.replace(/^www\./, ""); } catch { return null; } })() : null;
  const domainInWhitelist = crawlDomain && whitelist.some(d => {
    const clean = d.replace(/^https?:\/\//, "").replace(/\/.*$/, "").replace(/^www\./, "");
    return crawlDomain === clean || crawlDomain.endsWith("." + clean);
  });
  const urlMatchesLast = crawlInfo.lastUrl && crawlUrl === crawlInfo.lastUrl;

  const fetchDocuments = useCallback(async () => {
    if (!botId) { setLoading(false); return; }
    try {
      const { getKnowledgeDocuments } = await import("../api/botApi");
      const res = await getKnowledgeDocuments(botId);
      setDocuments(res.documents || []);
    } catch {} finally { setLoading(false); }
  }, [botId]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  // Poll crawl + queue status when crawling or queued
  useEffect(() => {
    if ((!crawling && !queued) || !botId) return;
    const interval = setInterval(async () => {
      try {
        const { getCrawlStatus, getCrawlQueueStatus, getBotCrawlInfo } = await import("../api/botApi");
        const [statusRes, queueRes] = await Promise.allSettled([
          getCrawlStatus(botId),
          getCrawlQueueStatus(botId),
        ]);

        if (statusRes.status === "fulfilled" && statusRes.value.session) {
          const s = statusRes.value.session;
          setCrawlSession(s);
          if (["completed", "stopped", "error"].includes(s.status)) {
            setCrawling(false);
            setQueued(false);
            setQueueInfo(null);
            clearInterval(interval);
            fetchDocuments();
            if (s.metadata?.noNewPages) {
              setNoNewPagesDialog(true);
            }
            getBotCrawlInfo(botId).then(res => setCrawlInfo(res)).catch(() => {});
          }
        }

        if (queueRes.status === "fulfilled") {
          setQueueInfo(queueRes.value);
          if (queueRes.value.myPosition && queueRes.value.myPosition > 0) {
            setQueued(true);
          } else {
            setQueued(false);
          }
        }
      } catch {}
    }, 2000);
    return () => clearInterval(interval);
  }, [crawling, queued, botId, fetchDocuments]);

  const handleStartCrawl = async () => {
    if (!botId || !crawlUrl.trim()) return;
    if (!crawlUrl.startsWith("http")) {
      toast.error("URL must start with http:// or https://");
      return;
    }
    if (!domainInWhitelist) {
      toast.error("Domain must be in your whitelist. Add it in Settings → Domain Whitelist first.");
      return;
    }
    setQueued(false);
    setQueueInfo(null);
    setCrawlSession(null);
    try {
      const { startCrawl } = await import("../api/botApi");
      const res = await startCrawl(botId, crawlUrl.trim(), crawlMax);
      if (res.totalAhead > 0) {
        setQueued(true);
      } else {
        setCrawling(true);
      }
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to start crawl");
      setCrawling(false);
    }
  };

  const handleStopCrawl = async () => {
    if (!botId) return;
    try {
      const { stopCrawl } = await import("../api/botApi");
      await stopCrawl(botId);
    } catch {}
  };

  const handleViewPages = async () => {
    if (!botId) return;
    try {
      const { getCrawledPages } = await import("../api/botApi");
      const res = await getCrawledPages(botId);
      setCrawlPages(res.pages || []);
      setShowCrawledPages(v => !v);
    } catch {}
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !botId) return;
    setUploading(true);
    try {
      const { uploadKnowledgeDocument } = await import("../api/botApi");
      await uploadKnowledgeDocument(botId, file);
      e.target.value = "";
      fetchDocuments();
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Upload failed");
    } finally { setUploading(false); }
  };

  const handleDelete = async (docName) => {
    if (!botId) return;
    setDeleteDialog({ open: true, docName });
  };

  const confirmDelete = async () => {
    const docName = deleteDialog.docName;
    setDeleteDialog({ open: false, docName: "" });
    if (!botId || !docName) return;
    try {
      const { deleteKnowledgeDocument } = await import("../api/botApi");
      await deleteKnowledgeDocument(botId, docName);
      fetchDocuments();
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Delete failed");
    }
  };

  const toggleExpand = async (docName) => {
    if (expandedDocs[docName]) {
      setExpandedDocs(p => ({ ...p, [docName]: !p[docName] }));
      return;
    }
    try {
      const { getKnowledgeDocumentChunks } = await import("../api/botApi");
      const res = await getKnowledgeDocumentChunks(botId, docName);
      setChunksCache(p => ({ ...p, [docName]: res.chunks || [] }));
      setExpandedDocs(p => ({ ...p, [docName]: true }));
    } catch {}
  };

  if (loading) return <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={24} /></Box>;

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography sx={{
          fontSize: 22, fontWeight: 800, color: "#111827", letterSpacing: "-0.4px",
          background: "linear-gradient(135deg, #1f2937, #0891b2)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
        }}>
          Knowledge Base
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: "#6b7280", mt: 0.5 }}>
          Upload PDF documents or crawl your website to give your AI bot additional knowledge. The AI will search these when answering visitor questions.
        </Typography>
      </Box>

      {/* Website Crawl Section */}
      <SectionCard accent="#0891b2" icon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
        title="Website Crawl"
        desc="Scrape your website content and add it to the AI bot's knowledge">
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 1 }}>
          <Box sx={{ flex: 1, minWidth: 280, position: "relative" }}>
            <TextField size="small" placeholder="https://example.com"
              value={crawlUrl} onChange={e => setCrawlUrl(e.target.value)}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "11px", bgcolor: "#f9fafb",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: crawlUrl && !crawlUrl.startsWith("http") ? "#ef4444" : crawlDomain && !domainInWhitelist ? "#ef4444" : urlMatchesLast ? "#10b981" : crawlDomain && domainInWhitelist ? "#10b981" : "#e5e7eb",
                    borderWidth: crawlDomain && (domainInWhitelist || !domainInWhitelist || urlMatchesLast) ? 2 : 1,
                  },
                }
              }}
              InputProps={{
                endAdornment: crawlDomain ? (
                  <Box component="span" sx={{ fontSize: 16, color: urlMatchesLast ? "#10b981" : domainInWhitelist ? "#10b981" : "#ef4444" }}>
                    {urlMatchesLast ? "✓" : domainInWhitelist ? "✓" : "✗"}
                  </Box>
                ) : null,
              }} />
          </Box>
          <Select size="small" value={crawlMax} onChange={e => setCrawlMax(Number(e.target.value))}
            sx={{
              minWidth: 100, borderRadius: "11px", bgcolor: "#f9fafb",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" },
            }}>
            <MenuItem value={50}>50 pages</MenuItem>
            <MenuItem value={100}>100 pages</MenuItem>
            <MenuItem value={200}>200 pages</MenuItem>
          </Select>
        </Box>

        {/* Whitelist chips */}
        {whitelist.length > 0 && (
          <Box sx={{ mb: 1.5, display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
            <Typography sx={{ fontSize: 11.5, color: "#6b7280", fontWeight: 600, mr: 0.5 }}>Whitelisted:</Typography>
            {whitelist.map((d, i) => (
              <Chip key={i} label={d} size="small" sx={{
                height: 22, fontSize: 11, fontWeight: 600,
                bgcolor: crawlDomain && domainInWhitelist ? "#d1fae5" : "#f3f4f6",
                color: crawlDomain && domainInWhitelist ? "#065f46" : "#6b7280",
              }} />
            ))}
          </Box>
        )}
        {whitelist.length === 0 && (
          <Typography sx={{ fontSize: 11.5, color: "#ef4444", mb: 1.5 }}>
            No domains whitelisted. Add domains in Settings → Domain Whitelist first.
          </Typography>
        )}

        {/* Previously crawled info */}
        {crawlInfo.lastUrl && (
          <Box sx={{ mb: 1.5, p: 1.5, bgcolor: "#ecfdf5", borderRadius: 2, border: "1px solid #d1fae5", display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircleIcon sx={{ fontSize: 18, color: "#10b981" }} />
            <Box>
              <Typography sx={{ fontSize: 12.5, color: "#065f46", fontWeight: 600 }}>
                Previously crawled: {crawlInfo.pageCount} page{crawlInfo.pageCount !== 1 ? "s" : ""}
              </Typography>
              {crawlInfo.lastCrawledAt && (
                <Typography sx={{ fontSize: 11, color: "#6b7280" }}>
                  Last crawled: {new Date(crawlInfo.lastCrawledAt).toLocaleDateString()}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Crawl controls */}
        {!crawling && !queued && (!crawlSession || crawlSession.status !== "running") && (
          <Button variant="contained" onClick={handleStartCrawl}
            disabled={!crawlUrl.trim() || !domainInWhitelist || whitelist.length === 0}
            startIcon={<AutoAwesomeIcon sx={{ fontSize: 18 }} />}
            sx={{
              borderRadius: "11px", textTransform: "none", fontWeight: 700, px: 3,
              background: "linear-gradient(135deg, #0891b2, #0e7490)",
              boxShadow: "0 4px 16px rgba(8,145,178,0.35)",
              "&:hover": { background: "linear-gradient(135deg, #0e7490, #155e75)" },
              "&.Mui-disabled": { background: "#d1d5db", boxShadow: "none" },
            }}>
            {crawlInfo.lastUrl ? "Re-crawl Website" : "Start Crawl"}
          </Button>
        )}

        {queued && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 1 }}>
              <HourglassEmptyIcon sx={{ fontSize: 20, color: "#f59e0b" }} />
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#374151" }}>
                Crawl queued — {queueInfo?.totalAhead > 0 ? `${queueInfo.totalAhead} ahead of you` : "processing your turn..."}
              </Typography>
              <Button size="small" variant="outlined" color="error" onClick={handleStopCrawl}
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: 12 }}>
                Cancel
              </Button>
            </Box>
            <Box sx={{ width: "100%", bgcolor: "#f3f4f6", borderRadius: 1.5, height: 6, mt: 1 }}>
              <Box sx={{
                width: queueInfo?.queueLength > 0 ? `${((queueInfo.queueLength - (queueInfo.myPosition || 0)) / queueInfo.queueLength) * 100}%` : "10%",
                height: 6, borderRadius: 1.5,
                background: "linear-gradient(90deg, #f59e0b, #fbbf24)",
                transition: "width 1s ease",
              }} />
            </Box>
          </Box>
        )}

        {crawling && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 1 }}>
              <CircularProgress size={20} sx={{ color: ACCENT }} />
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#374151" }}>
                Crawling... {crawlSession ? `${crawlSession.crawledUrls}/${crawlSession.totalUrls}` : "starting..."}
              </Typography>
              <Button size="small" variant="outlined" color="error" onClick={handleStopCrawl}
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: 12 }}>
                Stop
              </Button>
            </Box>
            {crawlSession?.currentUrl && (
              <Typography sx={{ fontSize: 11.5, color: "#9ca3af", fontStyle: "italic" }}>
                Current: {crawlSession.currentUrl}
              </Typography>
            )}
          </Box>
        )}

        {/* Crawl results */}
        {crawlSession && crawlSession.status === "completed" && !crawlSession.metadata?.noNewPages && (
          <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "#ecfdf5", borderRadius: 2, border: "1px solid #d1fae5" }}>
            <Typography sx={{ fontSize: 13, color: "#065f46", fontWeight: 600 }}>
              ✅ Crawled {crawlSession.crawledUrls} page{crawlSession.crawledUrls !== 1 ? "s" : ""} ({crawlSession.metadata?.newPagesCount || crawlSession.crawledUrls} new)
            </Typography>
            {crawlSession.erroredUrls > 0 && (
              <Typography sx={{ fontSize: 12, color: "#92400e", mt: 0.3 }}>
                ⚠️ {crawlSession.erroredUrls} pages failed
              </Typography>
            )}
          </Box>
        )}
        {crawlSession && crawlSession.status === "stopped" && (
          <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "#fef3c7", borderRadius: 2, border: "1px solid #fde68a" }}>
            <Typography sx={{ fontSize: 13, color: "#92400e", fontWeight: 600 }}>
              ⏹ Crawl stopped ({crawlSession.crawledUrls} pages crawled)
            </Typography>
          </Box>
        )}

        {/* View crawled pages */}
        {crawlSession && crawlSession.pages?.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Button size="small" onClick={handleViewPages}
              sx={{ textTransform: "none", fontWeight: 600, color: ACCENT, fontSize: 12.5 }}>
              {showCrawledPages ? "Hide Pages" : "View Crawled Pages"}
            </Button>
            {showCrawledPages && (
              <Box sx={{ mt: 1, maxHeight: 250, overflowY: "auto" }}>
                {crawlPages.map((p, idx) => (
                  <Box key={idx} sx={{
                    display: "flex", alignItems: "center", gap: 1, py: 0.6, px: 1.5,
                    borderRadius: 1.5, bgcolor: p.status === "crawled" ? "#f0fdf4" : p.status === "error" ? "#fef2f2" : "#f9fafb",
                    mb: 0.5, fontSize: 12, color: "#374151"
                  }}>
                    <Box sx={{
                      width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
                      bgcolor: p.status === "crawled" ? "#10b981" : p.status === "error" ? "#ef4444" : "#d1d5db",
                    }} />
                    <Typography sx={{ flex: 1, fontSize: 12, wordBreak: "break-all" }}>{p.url}</Typography>
                    {p.chunkCount > 0 && (
                      <Chip label={`${p.chunkCount}`} size="small" sx={{
                        height: 20, fontSize: 10, fontWeight: 700, bgcolor: "#e0f2fe", color: "#0369a1"
                      }} />
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </SectionCard>

      {/* PDF Upload Section */}
      <Box sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: "1px solid #a5f3fc",
        background: "linear-gradient(135deg, rgba(14,165,233,0.06), rgba(8,145,178,0.05))" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Button variant="contained" component="label" disabled={uploading}
            startIcon={uploading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <AddIcon />}
            sx={{ borderRadius: "11px", textTransform: "none", fontWeight: 700, px: 3,
              background: "linear-gradient(135deg, #0891b2, #0e7490)",
              boxShadow: "0 4px 16px rgba(8,145,178,0.35)",
              "&:hover": { background: "linear-gradient(135deg, #0e7490, #155e75)" },
              "&.Mui-disabled": { background: "#d1d5db", boxShadow: "none" } }}>
            {uploading ? "Uploading..." : "Upload PDF"}
            <input type="file" accept=".pdf" hidden onChange={handleUpload} />
          </Button>
          <Typography sx={{ fontSize: 12.5, color: "#6b7280" }}>Max 10MB per file</Typography>
        </Box>
      </Box>

      {documents.length === 0 && (
        <Box sx={{ textAlign: "center", py: 5, px: 3, bgcolor: "#f8fafc", borderRadius: 3, border: "1px dashed #cbd5e1", mb: 2.5 }}>
          <MenuBookIcon sx={{ fontSize: 40, color: "#94a3b8", mb: 1 }} />
          <Typography sx={{ fontSize: 14, color: "#64748b", mb: 0.5, fontWeight: 600 }}>No documents uploaded</Typography>
          <Typography sx={{ fontSize: 12.5, color: "#9ca3af" }}>
            Upload PDFs or crawl your website to give your bot extra knowledge. The AI will search these when answering questions.
          </Typography>
        </Box>
      )}

      {documents.map((doc) => (
        <SectionCard key={doc.name} accent={ACCENT}
          title={doc.name}
          desc={`${doc.chunks} chunk${doc.chunks > 1 ? "s" : ""}`}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button size="small" onClick={() => toggleExpand(doc.name)}
              sx={{ textTransform: "none", fontWeight: 600, color: ACCENT, fontSize: 12.5 }}>
              {expandedDocs[doc.name] ? "Hide Chunks" : "View Chunks"}
            </Button>
            <Button size="small" color="error" onClick={() => handleDelete(doc.name)}
              startIcon={<DeleteOutlineIcon fontSize="small" />}
              sx={{ textTransform: "none", fontWeight: 600 }}>
              Delete
            </Button>
          </Box>
          {expandedDocs[doc.name] && (
            <Box sx={{ mt: 2, maxHeight: 300, overflowY: "auto" }}>
              {(chunksCache[doc.name] || []).map((chunk, idx) => (
                <Box key={idx} sx={{ p: 1.5, mb: 1, borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                  <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, mb: 0.5 }}>
                    Chunk {chunk.chunkIndex + 1}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: "#374151", lineHeight: 1.5 }}>
                    {chunk.text?.slice(0, 300)}{chunk.text?.length > 300 ? "..." : ""}
                  </Typography>
                </Box>
              ))}
              {(chunksCache[doc.name] || []).length === 0 && (
                <Typography sx={{ fontSize: 12.5, color: "#9ca3af", textAlign: "center", py: 2 }}>
                  No chunks loaded
                </Typography>
              )}
            </Box>
          )}
        </SectionCard>
      ))}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, docName: "" })}
        PaperProps={{ sx: { borderRadius: 3, p: 0.5 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 17, pb: 0.5 }}>Delete Document</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: "#6b7280" }}>
            Delete <b>"{deleteDialog.docName}"</b>? This will remove all its chunks from the knowledge base.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setDeleteDialog({ open: false, docName: "" })}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, color: "#6b7280" }}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} variant="contained" color="error"
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* No new pages dialog */}
      <Dialog open={noNewPagesDialog} onClose={() => setNoNewPagesDialog(false)}
        PaperProps={{ sx: { borderRadius: 3, p: 0.5 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 17, pb: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
          <CheckCircleIcon sx={{ color: "#10b981", fontSize: 22 }} />
          Already Crawled
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: "#6b7280" }}>
            All pages from this website have already been crawled and saved. No new pages were found.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setNoNewPagesDialog(false)} variant="contained"
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2,
              background: "linear-gradient(135deg, #0891b2, #0e7490)" }}>
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default function SettingsPage() {
  const [active, setActive] = useTabSync("language");
  const activeNav = NAV.find((t) => t.id === active);

  return (
    <Box sx={{
      position: "relative", minHeight: "100vh", overflow: "hidden",
      "&::before": {
        content: '""', position: "absolute", inset: 0,
        background: `
          radial-gradient(600px circle at 0% 20%, rgba(79,70,229,0.06) 0%, transparent 70%),
          radial-gradient(500px circle at 80% 80%, rgba(124,58,237,0.05) 0%, transparent 70%),
          radial-gradient(400px circle at 50% 50%, rgba(99,102,241,0.03) 0%, transparent 70%)
        `,
        animation: "waterFlow 12s ease-in-out infinite",
        pointerEvents: "none",
      },
      "&::after": {
        content: '""', position: "absolute", inset: 0,
        background: "linear-gradient(180deg, #f8faff 0%, #f0f4ff 50%, #faf5ff 100%)",
        opacity: 0.7, pointerEvents: "none",
      },
      "@keyframes waterFlow": {
        "0%,100%": { backgroundPosition: "0% 0%" },
        "50%": { backgroundPosition: "100% 100%" },
      },
    }}>
      <Box sx={{ position: "relative", zIndex: 1, p: { xs: 1.5, md: 3 }, maxWidth: 1400, mx: "auto" }}>
        <Box sx={{ display: "flex", gap: { xs: 0, md: 3 }, alignItems: "stretch", flexDirection: { xs: "column", sm: "row" } }}>
          {/* Mobile tabs */}
          <Box sx={{ display: { xs: "flex", sm: "none" }, gap: 1, overflowX: "auto", pb: 1.5, "&::-webkit-scrollbar": { display: "none" } }}>
            {NAV.map(t => (
              <Chip key={t.id} label={t.label} size="small" onClick={() => setActive(t.id)}
                sx={{ flexShrink: 0, fontWeight: active === t.id ? 700 : 500, fontSize: 11,
                  bgcolor: active === t.id ? `${t.color}15` : '#fff',
                  border: `1px solid ${active === t.id ? t.color : '#e5e7eb'}`,
                  color: active === t.id ? t.color : '#6b7280',
                }} />
            ))}
          </Box>

          {/* Desktop sidebar */}
          <Paper elevation={0} sx={{
            width: 260, flexShrink: 0, alignSelf: "flex-start", position: "sticky", top: 24,
            display: { xs: "none", sm: "block" },
            borderRadius: 3.5, bgcolor: "rgba(255,255,255,0.88)", backdropFilter: "blur(16px)",
            border: "1px solid rgba(229,231,235,0.4)", overflow: "hidden",
            boxShadow: "0 4px 24px rgba(0,0,0,0.04)", transition: "all 0.3s",
          }}>
            <Box sx={{
              px: { xs: 1, sm: 2.5 }, py: 2.5,
              borderBottom: "1px solid rgba(243,244,246,0.8)",
              background: "linear-gradient(135deg, rgba(79,70,229,0.03), rgba(124,58,237,0.03))",
            }}>
              <Typography sx={{
                fontWeight: 800, fontSize: { xs: 13, sm: 17 },
                background: "linear-gradient(135deg, #4F46E5, #7C3AED)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                display: { xs: "none", sm: "block" },
                letterSpacing: "-0.3px",
              }}>
                Settings
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#9ca3af", mt: 0.3, display: { xs: "none", sm: "block" } }}>
                Configure your chatbot
              </Typography>
            </Box>
            <List sx={{ p: 1 }}>
              {NAV.map((t) => {
                const sel = active === t.id;
                return (
                  <ListItemButton
                    key={t.id}
                    onClick={() => setActive(t.id)}
                    selected={sel}
                    sx={{
                      borderRadius: 2, mb: 0.2, px: { xs: 1, sm: 1.5 }, py: 0.9,
                      position: "relative", overflow: "visible",
                      transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
                      "&.Mui-selected": {
                        bgcolor: `${t.color}0d`,
                        "&:hover": { bgcolor: `${t.color}14` },
                      },
                      "&:hover": { bgcolor: "rgba(243,244,246,0.8)" },
                      justifyContent: "flex-start",
                    }}
                  >
                    {sel && (
                      <Box sx={{
                        position: "absolute", left: -1, top: "50%", transform: "translateY(-50%)",
                        width: 3.5, height: 20, borderRadius: "0 4px 4px 0",
                        background: `linear-gradient(180deg, ${t.color}, ${t.color}cc)`,
                        boxShadow: `0 0 8px ${t.color}40`,
                      }} />
                    )}
                    <ListItemIcon sx={{
                      minWidth: { xs: 28, sm: 36 }, color: sel ? t.color : "#9ca3af",
                      justifyContent: "center", transition: "all 0.2s",
                    }}>
                      <Box sx={{
                        width: 32, height: 32, borderRadius: "11px",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        bgcolor: sel ? `${t.color}14` : "transparent",
                        transition: "all 0.25s",
                        fontSize: 20,
                        "& svg": { fontSize: sel ? 18 : 17 },
                      }}>
                        {t.icon}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={t.label}
                      primaryTypographyProps={{
                        fontSize: { xs: 11, sm: 13 }, fontWeight: sel ? 700 : 500,
                        color: sel ? t.color : "#374151",
                        whiteSpace: "nowrap",
                        letterSpacing: "-0.1px",
                      }}
                    />
                    {sel && (
                      <ChevronRightIcon sx={{
                        fontSize: 16, color: t.color,
                        display: { xs: "none", sm: "block" },
                        opacity: 0.6,
                      }} />
                    )}
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>

          <Paper elevation={0} sx={{
            flex: 1, borderRadius: 3.5, p: { xs: 2, md: 3.5 },
            bgcolor: "rgba(255,255,255,0.94)", backdropFilter: "blur(16px)",
            border: "1px solid rgba(229,231,235,0.3)",
            boxShadow: "0 4px 24px rgba(0,0,0,0.04)",
            minHeight: "75vh", transition: "all 0.3s",
            position: "relative",
            "&::before": {
              content: '""', position: "absolute", top: 0, left: 0, right: 0,
              height: 3, borderRadius: "3.5px 3.5px 0 0",
              background: "linear-gradient(90deg, #4F46E5, #7C3AED, #6366f1, #8b5cf6)",
              backgroundSize: "200% 100%",
              animation: "gradientShift 4s ease-in-out infinite",
            },
            "@keyframes gradientShift": {
              "0%,100%": { backgroundPosition: "0% 50%" },
              "50%": { backgroundPosition: "100% 50%" },
            },
          }}>
            {active === "language" && <LanguageTab />}
            {active === "autotrigger" && <AutoTriggerTab />}
            {active === "whitelist" && <WhitelistTab />}
            {active === "consent" && <ConsentTab />}
            {active === "security" && <SecurityTab />}
            {active === "aiBot" && <AiBotTab />}
            {active === "canned" && <CannedTab />}
            {active === "helpCenter" && <HelpCenterTab />}
            {active === "knowledgeBase" && <KnowledgeBaseTab />}
            {active === "contactCollection" && <ContactCollectionTab />}
            {active === "emailAutomation" && <EmailAutomationTab />}
            {active === "instagram" && <InstagramTab />}
            {!["language", "autotrigger", "whitelist", "consent", "security", "aiBot", "canned", "helpCenter", "knowledgeBase", "contactCollection", "emailAutomation", "instagram"].includes(active) && (
              <Box sx={{ height: "100%", minHeight: 500, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2.5 }}>
                <Box sx={{
                  width: 72, height: 72, borderRadius: "20px",
                  background: `linear-gradient(135deg, ${activeNav?.color}15, ${activeNav?.color}08)`,
                  border: `1px solid ${activeNav?.color}20`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: activeNav?.color, fontSize: 32,
                  boxShadow: `0 8px 32px ${activeNav?.color}15`,
                }}>
                  {activeNav?.icon}
                </Box>
                <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#111827", letterSpacing: "-0.3px" }}>{activeNav?.label}</Typography>
                <Typography sx={{ fontSize: 14, color: "#9ca3af", maxWidth: 360, textAlign: "center", lineHeight: 1.6 }}>
                  Settings for <b style={{ color: "#4F46E5" }}>{activeNav?.label}</b> will appear here. Stay tuned!
                </Typography>
                <Chip label="Coming Soon" size="small" sx={{
                  borderRadius: 2, bgcolor: "#fef3c7", color: "#d97706", fontWeight: 700, fontSize: 12, px: 0.5,
                  border: "1px solid #fde68a",
                  animation: "pulseGlow 2s ease-in-out infinite",
                }} />
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
      <ToastContainer />
    </Box>
  );
}

function SectionCard({ icon, title, desc, children, accent }) {
  return (
    <Box sx={{
      bgcolor: "#fff", borderRadius: 3, p: 3, mb: 2.5,
      border: "1px solid rgba(229,231,235,0.5)", transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
      position: "relative", overflow: "hidden",
      boxShadow: "0 1px 4px rgba(0,0,0,0.03)",
      "&:hover": {
        borderColor: accent ? `${accent}30` : "rgba(79,70,229,0.2)",
        boxShadow: `0 8px 30px ${accent ? `${accent}12` : "rgba(79,70,229,0.08)"}`,
        transform: "translateY(-1px)",
      },
      "&::before": {
        content: '""', position: "absolute", top: 0, left: 0, width: 4, height: "100%",
        background: `linear-gradient(180deg, ${accent || "#4F46E5"}, ${accent || "#7C3AED"}cc)`,
        opacity: 0.6, borderRadius: "0 2px 2px 0",
      },
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5, ml: 0.5 }}>
        {icon && (
          <Box sx={{
            color: accent || "#4F46E5", display: "flex",
            width: 28, height: 28, borderRadius: "8px",
            bgcolor: `${accent || "#4F46E5"}0d`,
            alignItems: "center", justifyContent: "center", fontSize: 16,
            flexShrink: 0,
          }}>
            {icon}
          </Box>
        )}
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#111827", letterSpacing: "-0.2px" }}>{title}</Typography>
          {desc && <Typography sx={{ fontSize: 12, color: "#9ca3af", mt: 0.1 }}>{desc}</Typography>}
        </Box>
      </Box>
      <Box sx={{ ml: 0.5 }}>{children}</Box>
    </Box>
  );
}

function FormRow({ label, hint, children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2.5, mb: 2.5, flexWrap: "wrap" }}>
      <Box sx={{ minWidth: 170, flex: "0 0 auto", pt: 0.5 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#374151", letterSpacing: "-0.1px" }}>{label}</Typography>
        {hint && <Typography sx={{ fontSize: 11.5, color: "#9ca3af", mt: 0.2 }}>{hint}</Typography>}
      </Box>
      <Box sx={{ flex: 1, minWidth: 220 }}>{children}</Box>
    </Box>
  );
}

function LanguageTab() {
  const [language, setLanguage] = useState("English");
  const [prefStatement, setPrefStatement] = useState("Please choose a language of your preference.");
  const [saving, setSaving] = useState(false);
  const locked = false;
  const navigate = useNavigate();
  const botId = localStorage.getItem('selectedBotId');

  useEffect(() => {
    if (!botId) return;
    getBotLanguage(botId).then(res => {
      setLanguage(res.language);
      setPrefStatement(res.prefStatement);
    }).catch(() => {});
  }, [botId]);

  const handleSave = async () => {
    if (!botId) return toast.error("Select a bot first");
    setSaving(true);
    try {
      await saveBotLanguage(botId, language, prefStatement);
      toast.success('Language settings saved!');
    } catch (e) { toast.error(e?.message || 'Failed to save language settings'); }
    finally { setSaving(false); }
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography sx={{
          fontSize: 22, fontWeight: 800, color: "#111827",
          letterSpacing: "-0.4px",
          background: "linear-gradient(135deg, #1f2937, #4F46E5)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
        }}>
          Language Settings
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: "#6b7280", mt: 0.5 }}>
          Configure the default language for your chatbot.
        </Typography>
      </Box>

      <SectionCard title="Default Language" desc="Choose the primary language for bot responses." accent="#4F46E5">
        <FormRow label="Select Language">
          <Select size="small" value={language} onChange={(e) => setLanguage(e.target.value)}
            sx={{
              minWidth: 280, bgcolor: "#f9fafb", borderRadius: "11px",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#4F46E5" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#4F46E5", borderWidth: 2 },
              transition: "all 0.2s",
            }}
            disabled={locked}>
            {["English","Hindi","Bengali","Marathi","Gujarati","Tamil","Telugu","Kannada","Malayalam","Punjabi"].map((lng) => (
              <MenuItem key={lng} value={lng}>{lng}</MenuItem>
            ))}
          </Select>
        </FormRow>
      </SectionCard>

      <SectionCard title="Language Preference Statement" desc="Message shown to users when asking language preference." accent="#7C3AED">
        <TextField placeholder="Please choose a language of your preference." value={prefStatement}
          onChange={(e) => setPrefStatement(e.target.value)} multiline minRows={3} fullWidth
          disabled={locked}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "11px", bgcolor: "#f9fafb",
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#7C3AED" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#7C3AED", borderWidth: 2 },
            },
          }} />
      </SectionCard>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 3 }}>
        <Button variant="contained" onClick={handleSave} disabled={saving || locked}
          sx={{
            borderRadius: "11px", textTransform: "none", fontWeight: 700, fontSize: 14,
            px: 4.5, py: 1.2,
            background: "linear-gradient(135deg, #4F46E5, #7C3AED)",
            boxShadow: "0 4px 16px rgba(79,70,229,0.35)",
            "&:hover": {
              background: "linear-gradient(135deg, #4338CA, #6D28D9)",
              boxShadow: "0 6px 24px rgba(79,70,229,0.45)",
              transform: "translateY(-1px)",
            },
            "&:active": { transform: "translateY(0)" },
            "&.Mui-disabled": { background: "#d1d5db", boxShadow: "none" },
            transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
          }}
          startIcon={saving ? undefined : <SaveIcon sx={{ fontSize: 18 }} />}>
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </Box>

      {locked && (
        <Box sx={{ textAlign: "center", mt: 5, p: 3.5, bgcolor: "#fef3c7", borderRadius: 3, border: "1px solid #fde68a" }}>
          <Typography sx={{ color: "#92400e", mb: 2, fontWeight: 600, fontSize: 14 }}>
            This feature requires an upgrade.
          </Typography>
          <Button variant="contained" startIcon={<UpgradeIcon />}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: "#d97706", "&:hover": { bgcolor: "#b45309" }, px: 3 }}>
            Upgrade Now
          </Button>
        </Box>
      )}
    </Box>
  );
}

function AutoTriggerTab() {
  const [desktopEnabled, setDesktopEnabled] = useState(true);
  const [mobileEnabled, setMobileEnabled] = useState(true);
  const [exitEnabled, setExitEnabled] = useState(false);
  const [desktopSeconds, setDesktopSeconds] = useState(5);
  const [mobileSeconds, setMobileSeconds] = useState(3);
  const [saving, setSaving] = useState(false);
  const toastId = 'autoTriggerTab';

  const botId = localStorage.getItem('selectedBotId');

  useEffect(() => {
    if (!botId) return;
    import("../api/botApi").then(({ getBotById }) => {
      getBotById(botId).then(data => {
        const t = data?.autoTrigger;
        if (t) {
          setDesktopEnabled(t.desktop ?? true);
          setMobileEnabled(t.mobile ?? true);
          setExitEnabled(t.exitIntent ?? false);
          setDesktopSeconds(t.desktopDelay ?? 5);
          setMobileSeconds(t.mobileDelay ?? 3);
        }
      }).catch(() => {});
    });
  }, [botId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { updateBot } = await import("../api/botApi");
      await updateBot(botId, { autoTrigger: { desktop: desktopEnabled, mobile: mobileEnabled, exitIntent: exitEnabled, desktopDelay: desktopSeconds, mobileDelay: mobileSeconds } });
      toast.success('Settings saved!');
    } catch (e) {
      toast.error(e?.message || 'Failed to save auto-trigger settings');
    } finally { setSaving(false); }
  };

  const toggleRow = (label, checked, onChange) => (
    <Box sx={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      p: 1.5, px: 2.5, borderRadius: 2.5,
      bgcolor: "#f9fafb", border: "1px solid #f3f4f6",
      mb: 1.5, transition: "all 0.2s",
      "&:hover": { borderColor: "#e5e7eb", bgcolor: "#fff" },
    }}>
      <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#374151" }}>{label}</Typography>
      <Switch checked={checked} onChange={onChange}
        sx={{
          "& .MuiSwitch-track": { bgcolor: checked ? "#c7d2fe" : "#e5e7eb" },
          "& .MuiSwitch-thumb": { bgcolor: checked ? "#4F46E5" : "#bdbdbd" },
        }} />
    </Box>
  );

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography sx={{
          fontSize: 22, fontWeight: 800, color: "#111827",
          letterSpacing: "-0.4px",
          background: "linear-gradient(135deg, #1f2937, #f59e0b)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
        }}>
          Auto Trigger
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: "#6b7280", mt: 0.5 }}>
          Control when the chatbot opens automatically for visitors.
        </Typography>
      </Box>

      <SectionCard title="Trigger Conditions" desc="Choose when the bot should auto-open." accent="#f59e0b">
        {toggleRow("In Desktop", desktopEnabled, (e) => setDesktopEnabled(e.target.checked))}
        {toggleRow("In Mobile", mobileEnabled, (e) => setMobileEnabled(e.target.checked))}
        {toggleRow("Before visitor exits", exitEnabled, (e) => setExitEnabled(e.target.checked))}
      </SectionCard>

      <SectionCard title="Trigger Timing" desc="Set delay before the bot opens (in seconds)." accent="#f97316">
        <FormRow label="Desktop delay">
          <TextField type="number" value={desktopSeconds} onChange={(e) => setDesktopSeconds(Number(e.target.value))}
            size="small" sx={{ maxWidth: 200, "& .MuiOutlinedInput-root": { borderRadius: "11px", bgcolor: "#f9fafb" } }} />
        </FormRow>
        <FormRow label="Mobile delay">
          <TextField type="number" value={mobileSeconds} onChange={(e) => setMobileSeconds(Number(e.target.value))}
            size="small" sx={{ maxWidth: 200, "& .MuiOutlinedInput-root": { borderRadius: "11px", bgcolor: "#f9fafb" } }} />
        </FormRow>
      </SectionCard>

      <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}
        sx={{
          borderRadius: "11px", textTransform: "none", fontWeight: 700, fontSize: 14,
          px: 4.5, py: 1.2, mt: 1,
          background: "linear-gradient(135deg, #f59e0b, #f97316)",
          boxShadow: "0 4px 16px rgba(245,158,11,0.35)",
          "&:hover": {
            background: "linear-gradient(135deg, #d97706, #ea580c)",
            boxShadow: "0 6px 24px rgba(245,158,11,0.45)",
            transform: "translateY(-1px)",
          },
          "&:active": { transform: "translateY(0)" },
          "&.Mui-disabled": { background: "#d1d5db", boxShadow: "none" },
          transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
        }}>
        {saving ? "Saving..." : "Save Settings"}
      </Button>
    </Box>
  );
}

function ConsentTab() {
  const CONSENT_MODES = [
    { id: "all", label: "For All Countries", desc: "Show the consent notice to every visitor before they start chatting." },
    { id: "europe", label: "Only For Europe", desc: "Show the consent notice only to visitors from the EU/EEA (GDPR)." },
    { id: "disabled", label: "Disabled", desc: "Never show a consent notice to visitors." },
  ];
  const DEFAULT_MESSAGE = "We use cookies and process your data to provide and improve our chat service. By continuing, you agree to our Privacy Policy.";

  const [mode, setMode] = useState("disabled");
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [privacyUrl, setPrivacyUrl] = useState("");
  const [buttonText, setButtonText] = useState("I Agree");
  const [saving, setSaving] = useState(false);

  const botId = localStorage.getItem("selectedBotId");

  useEffect(() => {
    if (!botId) return;
    import("../api/botApi").then(({ getBotById }) => {
      getBotById(botId).then(data => {
        const c = data?.botSettings?.consent;
        if (c) {
          setMode(c.mode || "disabled");
          setMessage(c.message ?? DEFAULT_MESSAGE);
          setPrivacyUrl(c.privacyUrl || "");
          setButtonText(c.buttonText || "I Agree");
        }
      }).catch(() => {});
    });
  }, [botId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!botId) { toast.error("No bot selected. Select a bot first."); return; }
    setSaving(true);
    try {
      const { updateBot } = await import("../api/botApi");
      await updateBot(botId, {
        botSettings: {
          consent: { mode, message: message.trim(), privacyUrl: privacyUrl.trim(), buttonText: buttonText.trim() || "I Agree" },
        },
      });
      toast.success('Consent settings saved!');
    } catch (e) {
      toast.error(typeof e === "string" ? e : "Failed to save consent settings");
    } finally { setSaving(false); }
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography sx={{
          fontSize: 22, fontWeight: 800, color: "#111827", letterSpacing: "-0.4px",
          background: "linear-gradient(135deg, #1f2937, #8b5cf6)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
        }}>
          Consent
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: "#6b7280", mt: 0.5 }}>
          Show a privacy / GDPR consent notice to visitors before they chat.
        </Typography>
      </Box>

      <SectionCard icon={<PolicyIcon sx={{ fontSize: 16 }} />} title="Consent Settings" desc="Choose where the consent notice should appear." accent="#8b5cf6">
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {CONSENT_MODES.map((m) => {
            const selected = mode === m.id;
            return (
              <Box key={m.id} onClick={() => setMode(m.id)}
                sx={{
                  display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer",
                  p: 2, borderRadius: 2.5, transition: "all 0.2s",
                  border: "2px solid", borderColor: selected ? "#8b5cf6" : "#eef0f4",
                  bgcolor: selected ? "#f5f3ff" : "#fff",
                  "&:hover": { borderColor: "#8b5cf6" },
                }}>
                {selected
                  ? <CheckCircleIcon sx={{ fontSize: 22, color: "#8b5cf6", flexShrink: 0 }} />
                  : <Box sx={{ width: 20, height: 20, borderRadius: "50%", border: "2px solid #d1d5db", flexShrink: 0 }} />}
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>{m.label}</Typography>
                  <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.2 }}>{m.desc}</Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </SectionCard>

      {mode !== "disabled" && (
        <SectionCard title="Notice Content" desc="What the visitor sees in the consent notice." accent="#7c3aed">
          <FormRow label="Consent message" hint="Shown above the agree button.">
            <TextField fullWidth multiline minRows={3} value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder={DEFAULT_MESSAGE}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "11px", bgcolor: "#f9fafb", fontSize: 13 } }} />
          </FormRow>
          <FormRow label="Privacy policy URL" hint="Optional — linked in the notice.">
            <TextField fullWidth value={privacyUrl} onChange={(e) => setPrivacyUrl(e.target.value)}
              placeholder="https://yoursite.com/privacy" size="small"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "11px", bgcolor: "#f9fafb", fontSize: 13 } }} />
          </FormRow>
          <FormRow label="Button label">
            <TextField value={buttonText} onChange={(e) => setButtonText(e.target.value)}
              placeholder="I Agree" size="small"
              sx={{ maxWidth: 240, "& .MuiOutlinedInput-root": { borderRadius: "11px", bgcolor: "#f9fafb", fontSize: 13 } }} />
          </FormRow>
        </SectionCard>
      )}

      <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}
        sx={{
          borderRadius: "11px", textTransform: "none", fontWeight: 700, fontSize: 14,
          px: 4.5, py: 1.2, mt: 1,
          background: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
          boxShadow: "0 4px 16px rgba(139,92,246,0.35)",
          "&:hover": {
            background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
            boxShadow: "0 6px 24px rgba(139,92,246,0.45)",
            transform: "translateY(-1px)",
          },
          "&:active": { transform: "translateY(0)" },
          "&.Mui-disabled": { background: "#d1d5db", boxShadow: "none" },
          transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
        }}>
        {saving ? "Saving..." : "Save"}
      </Button>
    </Box>
  );
}

function WhitelistTab() {
  const [domains, setDomains] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const botId = localStorage.getItem('selectedBotId');

  useEffect(() => {
    if (!botId) return;
    getBotWhitelist(botId).then(res => {
      if (res.whitelist?.length) setDomains(res.whitelist.join("\n"));
    });
  }, [botId]);

  const handleSave = async () => {
    if (!botId) return toast.error("Select a bot first");
    setLoading(true);
    try {
      await saveBotWhitelist(botId, domains);
      toast.success('Whitelist saved!');
      navigate("/app/install?refresh=1");
    } catch (e) { toast.error(e?.message || 'Failed to save whitelist'); }
    finally { setLoading(false); }
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography sx={{
          fontSize: 22, fontWeight: 800, color: "#111827",
          letterSpacing: "-0.4px",
          background: "linear-gradient(135deg, #1f2937, #10b981)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
        }}>
          Domain Whitelist
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: "#6b7280", mt: 0.5 }}>
          Only allow your chatbot to load on these domains. One per line.
        </Typography>
      </Box>

      <SectionCard title="Allowed Domains" desc="Add domains where your chatbot widget can appear." accent="#10b981">
        <TextField multiline minRows={5}
          placeholder={`https://example.com\nhttps://abc.com`}
          value={domains} onChange={(e) => setDomains(e.target.value)}
          fullWidth
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "11px", bgcolor: "#f9fafb", fontFamily: "monospace", fontSize: 13,
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#10b981" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#10b981", borderWidth: 2 },
            },
          }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2.5 }}>
          <Button variant="contained" onClick={handleSave} disabled={loading}
            sx={{
              borderRadius: "11px", textTransform: "none", fontWeight: 700, fontSize: 14,
              px: 4.5, py: 1.2,
              background: "linear-gradient(135deg, #10b981, #34d399)",
              boxShadow: "0 4px 16px rgba(16,185,129,0.35)",
              "&:hover": {
                background: "linear-gradient(135deg, #059669, #10b981)",
                boxShadow: "0 6px 24px rgba(16,185,129,0.45)",
                transform: "translateY(-1px)",
              },
              "&:active": { transform: "translateY(0)" },
              "&.Mui-disabled": { background: "#d1d5db", boxShadow: "none" },
              transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
            }}
            startIcon={loading ? undefined : <SaveIcon sx={{ fontSize: 18 }} />}>
            {loading ? "Saving..." : "Save"}
          </Button>
        </Box>
      </SectionCard>
    </Box>
  );
}

// ─── Security Tab (2FA) ───────────────────────
const TF_API = (process.env.REACT_APP_AUTH_API || "http://localhost:5000/api/auth").replace("/api/auth", "/api/2fa");

function SecurityTab() {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [qrUrl, setQrUrl] = useState(null);
  const [secret, setSecret] = useState(null);
  const [token, setToken] = useState("");
  const [step, setStep] = useState("idle");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [upgradeBlocked, setUpgradeBlocked] = useState(null);

  useEffect(() => {
    fetch(`${TF_API}/status`, { credentials: "include" })
      .then(async (r) => {
        if (r.status === 403) {
          const data = await r.json();
          if (data.upgrade) setUpgradeBlocked({ feature: "2FA", plan: data.plan });
          return;
        }
        const d = await r.json();
        setEnabled(d.enabled);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSetup = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${TF_API}/generate`, {
        method: "POST",
        credentials: "include",
      });
      if (res.status === 403) {
        const data = await res.json();
        if (data.upgrade) setUpgradeBlocked({ feature: "2FA", plan: data.plan });
        return;
      }
      const data = await res.json();
      setQrUrl(data.qrUrl);
      setSecret(data.secret);
      setStep("verify");
    } catch { setMessage("Failed to generate 2FA"); }
    finally { setSaving(false); }
  };

  const handleVerify = async () => {
    if (!token) return;
    setSaving(true);
    try {
      const res = await fetch(`${TF_API}/verify`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (data.enabled) {
        setEnabled(true);
        setStep("done");
      } else {
        setMessage(data.message || "Verification failed");
      }
    } catch { setMessage("Verification failed"); }
    finally { setSaving(false); }
  };

  const handleDisable = async () => {
    setSaving(true);
    try {
      await fetch(`${TF_API}/disable`, {
        method: "POST",
        credentials: "include",
      });
      setEnabled(false);
      setStep("idle");
      setQrUrl(null);
      setSecret(null);
      setToken("");
      setMessage("2FA disabled");
    } catch { setMessage("Failed to disable"); }
    finally { setSaving(false); }
  };

  if (loading) return <CircularProgress sx={{ display: "block", mx: "auto", my: 4 }} />;

  if (upgradeBlocked) {
    return (
      <SectionCard accent="#ef4444">
        <Box sx={{ p: 3 }}>
          <Typography fontWeight={700} fontSize={17} mb={2}>Two-Factor Authentication</Typography>
          <PlanUpgrade feature={upgradeBlocked.feature} plan={upgradeBlocked.plan} />
        </Box>
      </SectionCard>
    );
  }

  return (
    <SectionCard accent="#ef4444">
      <Box sx={{ p: 3 }}>
        <Typography fontWeight={700} fontSize={17} mb={0.5}>Two-Factor Authentication</Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Add an extra layer of security to your account using an authenticator app.
        </Typography>

        {step === "done" && (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <Box sx={{ width: 56, height: 56, borderRadius: "50%", bgcolor: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 28, color: "#10b981" }} />
            </Box>
            <Typography fontWeight={700} fontSize={16} mb={1}>2FA is Active</Typography>
            <Button variant="outlined" color="error" onClick={handleDisable} disabled={saving}
              sx={{ textTransform: "none", borderRadius: 2, mt: 1 }}>
              Disable 2FA
            </Button>
          </Box>
        )}

        {step === "verify" && qrUrl && (
          <Box sx={{ textAlign: "center", py: 2 }}>
            <Typography fontWeight={600} fontSize={14} mb={2}>Scan this QR Code with your authenticator app</Typography>
            <Box component="img" src={qrUrl} alt="2FA QR Code"
              sx={{ width: 180, height: 180, borderRadius: 2, mb: 2, border: "1px solid #e5e7eb" }} />
            {secret && (
              <Typography variant="caption" color="#6b7280" sx={{ display: "block", mb: 2, wordBreak: "break-all", fontFamily: "monospace", fontSize: 11 }}>
                Secret: {secret}
              </Typography>
            )}
            <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", mt: 1 }}>
              <TextField size="small" placeholder="Enter 6-digit code" value={token} onChange={(e) => setToken(e.target.value)}
                sx={{ width: 180 }}
                InputProps={{ sx: { borderRadius: 2, fontSize: 14, textAlign: "center", letterSpacing: 2 } }} />
              <Button variant="contained" onClick={handleVerify} disabled={saving || token.length < 6}
                sx={{ textTransform: "none", borderRadius: 2, bgcolor: "#ef4444", "&:hover": { bgcolor: "#dc2626" } }}>
                Verify & Enable
              </Button>
            </Box>
          </Box>
        )}

        {step === "idle" && !enabled && (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <LockIcon sx={{ fontSize: 40, color: "#ef4444", mb: 1 }} />
            <Typography variant="body2" color="#6b7280" mb={2}>2FA is not enabled yet</Typography>
            <Button variant="contained" onClick={handleSetup} disabled={saving}
              sx={{ textTransform: "none", borderRadius: 2, bgcolor: "#ef4444", "&:hover": { bgcolor: "#dc2626" } }}>
              Set Up 2FA
            </Button>
          </Box>
        )}

        {message && (
          <Typography variant="caption" color={message.includes("failed") ? "error" : "#10b981"} sx={{ display: "block", textAlign: "center", mt: 2 }}>
            {message}
          </Typography>
        )}
      </Box>
    </SectionCard>
  );
}




function AiBotTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ai, setAi] = useState({ enabled: false, openaiKey: '', systemPrompt: '', model: 'gpt-3.5-turbo', brandName: '' });
  const [greetingMessage, setGreetingMessage] = useState('');
  const botId = localStorage.getItem("selectedBotId");

  useEffect(() => {
    if (!botId) return;
    import("../api/botApi").then(({ getBotById }) => {
      getBotById(botId).then(data => {
        if (data?.ai) setAi(data.ai);
        if (data?.botSettings?.greetingMessage) setGreetingMessage(data.botSettings.greetingMessage);
      }).catch(() => {}).finally(() => setLoading(false));
    });
  }, [botId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { updateBot } = await import("../api/botApi");
      await updateBot(botId, { ai, botSettings: { greetingMessage: greetingMessage.trim() } });
      toast.success('Settings saved!');
    } catch (e) {
      toast.error(e?.message || 'Failed to save settings');
    } finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={24} /></Box>;

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Greeting Message — always visible */}
      <Box sx={{ p: 2.5, bgcolor: "#f8fafc", borderRadius: 3, border: "1px solid #e2e8f0" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>Greeting Message</Typography>
        </Box>
        <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 1.5 }}>
          Sent to each visitor when they start a new conversation. Leave empty to disable.
        </Typography>
        <TextField fullWidth multiline minRows={2} maxRows={4} size="small"
          placeholder="e.g. Welcome! How can I help you today?"
          value={greetingMessage} onChange={e => setGreetingMessage(e.target.value)}
          inputProps={{ maxLength: 500 }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }} />
        <Typography sx={{ fontSize: 11, color: "#9ca3af", mt: 0.5 }}>{greetingMessage.length}/500 characters</Typography>
      </Box>

      <Box sx={{ p: 2.5, bgcolor: "#f8fafc", borderRadius: 3, border: "1px solid #e2e8f0" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>Enable AI Bot</Typography>
            <Typography sx={{ fontSize: 12, color: "#6b7280" }}>After flow completes, AI will respond to visitor messages</Typography>
          </Box>
          <Switch checked={ai.enabled} onChange={e => setAi({ ...ai, enabled: e.target.checked })} color="primary" />
        </Box>
      </Box>

      {ai.enabled && (
        <>
          <Box sx={{ p: 2.5, bgcolor: "#f8fafc", borderRadius: 3, border: "1px solid #e2e8f0" }}>
            <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1, color: "#1f2937" }}>Industry</Typography>
            <Select fullWidth size="small" value={ai.industry || ''}
              onChange={e => setAi({ ...ai, industry: e.target.value })}
              displayEmpty sx={{ borderRadius: 2, bgcolor: "#fff" }}>
              <MenuItem value="">Select Industry</MenuItem>
              <MenuItem value="Digital Marketing">Digital Marketing</MenuItem>
              <MenuItem value="E-commerce">E-commerce</MenuItem>
              <MenuItem value="Healthcare">Healthcare</MenuItem>
              <MenuItem value="Education">Education</MenuItem>
              <MenuItem value="Real Estate">Real Estate</MenuItem>
              <MenuItem value="Finance & Banking">Finance & Banking</MenuItem>
              <MenuItem value="Travel & Hospitality">Travel & Hospitality</MenuItem>
              <MenuItem value="SaaS / Technology">SaaS / Technology</MenuItem>
              <MenuItem value="Legal">Legal</MenuItem>
              <MenuItem value="Fitness & Wellness">Fitness & Wellness</MenuItem>
              <MenuItem value="Food & Restaurant">Food & Restaurant</MenuItem>
              <MenuItem value="Automotive">Automotive</MenuItem>
              <MenuItem value="Insurance">Insurance</MenuItem>
              <MenuItem value="Recruitment / HR">Recruitment / HR</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
            <Typography sx={{ fontSize: 11, color: "#9ca3af", mt: 0.5 }}>AI will only answer questions related to this industry</Typography>
          </Box>

          <Box sx={{ p: 2.5, bgcolor: "#f8fafc", borderRadius: 3, border: "1px solid #e2e8f0" }}>
            <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1, color: "#1f2937" }}>Brand Name</Typography>
            <Typography sx={{ fontSize: 11, color: "#6b7280", mb: 1.5 }}>Shown in scheduling confirmation emails (e.g. "Meeting Confirmed — YourBrand")</Typography>
            <TextField fullWidth size="small" placeholder="Talktokam" value={ai.brandName || ''}
              onChange={e => setAi({ ...ai, brandName: e.target.value })}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }} />
          </Box>

          <Box sx={{ p: 2.5, bgcolor: "#f8fafc", borderRadius: 3, border: "1px solid #e2e8f0" }}>
            <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1, color: "#1f2937" }}>OpenAI API Key (Optional)</Typography>
            <TextField fullWidth size="small" type="password" placeholder="sk-..." value={ai.openaiKey}
              onChange={e => setAi({ ...ai, openaiKey: e.target.value })}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }} />
            <Typography sx={{ fontSize: 11, color: "#9ca3af", mt: 0.5 }}>Get your key from platform.openai.com</Typography>
          </Box>

          <Box sx={{ p: 2.5, bgcolor: "#f8fafc", borderRadius: 3, border: "1px solid #e2e8f0" }}>
            <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1, color: "#1f2937" }}>AI Model</Typography>
            <Select fullWidth size="small" value={ai.model || 'gpt-3.5-turbo'}
              onChange={e => setAi({ ...ai, model: e.target.value })}
              sx={{ borderRadius: 2, bgcolor: "#fff" }}>
              <MenuItem value="gpt-3.5-turbo">GPT-3.5 Turbo (Fast, Cheap)</MenuItem>
              <MenuItem value="gpt-4o-mini">GPT-4o Mini (Smart, Affordable)</MenuItem>
              <MenuItem value="gpt-4o">GPT-4o (Most Capable)</MenuItem>
            </Select>
          </Box>

          <Box sx={{ p: 2.5, bgcolor: "#f8fafc", borderRadius: 3, border: "1px solid #e2e8f0" }}>
            <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1, color: "#1f2937" }}>System Prompt</Typography>
            <TextField fullWidth multiline minRows={3} maxRows={6} size="small" placeholder="You are a helpful customer support assistant..."
              value={ai.systemPrompt} onChange={e => setAi({ ...ai, systemPrompt: e.target.value })}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }} />
            <Typography sx={{ fontSize: 11, color: "#9ca3af", mt: 0.5 }}>Tell the AI who it is and how to behave</Typography>
          </Box>

          <Box sx={{ p: 2, bgcolor: "#ede9fe", borderRadius: 2, border: "1px solid #ddd6fe" }}>
            <Typography sx={{ fontSize: 12, color: "#6d28d9" }}>
              💡 AI will activate after the chatbot flow completes. Visitor can then chat freely with AI.
            </Typography>
          </Box>
        </>
      )}

      <Button variant="contained" onClick={handleSave} disabled={saving}
        sx={{ alignSelf: "flex-start", borderRadius: 2, textTransform: "none", fontWeight: 700, px: 4,
          background: "linear-gradient(135deg, #6366f1, #8b5cf6)" }}>
        {saving ? "Saving..." : "Save AI Settings"}
      </Button>
    </Box>
  );
}

function CannedTab() {
  const [list, setList] = useState([]);
  const [shortcut, setShortcut] = useState('');
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const base = process.env.REACT_APP_AUTH_API ? process.env.REACT_APP_AUTH_API.replace('/auth', '') : 'http://localhost:5000/api';

  const load = () => fetch(`${base}/canned`, { credentials: "include" })
    .then(r => r.json()).then(setList).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!shortcut.trim() || !text.trim()) return;
    try {
      await fetch(`${base}/canned`, { method: 'POST', credentials: "include", headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ shortcut, text }) });
      toast.success('Canned response added!');
      setShortcut(''); setText(''); load();
    } catch (e) { toast.error('Failed to add canned response'); }
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${base}/canned/${id}`, { method: 'DELETE', credentials: "include" });
      toast.success('Canned response deleted');
      load();
    } catch (e) { toast.error('Failed to delete'); }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>;

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box sx={{ p: 2.5, bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1.5 }}>Add Canned Response</Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField size="small" placeholder="/shortcut" value={shortcut} onChange={e => setShortcut(e.target.value)}
            sx={{ width: 140, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} />
          <TextField size="small" fullWidth placeholder="Response text..." value={text} onChange={e => setText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} />
          <Button variant="contained" onClick={handleAdd} disabled={!shortcut.trim() || !text.trim()}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, bgcolor: '#10b981', whiteSpace: 'nowrap' }}>Add</Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {list.length === 0 && <Typography sx={{ fontSize: 13, color: '#9ca3af', textAlign: 'center', py: 3 }}>No canned responses yet</Typography>}
        {list.map(item => (
          <Box key={item._id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <Chip label={`/${item.shortcut}`} size="small" sx={{ fontWeight: 700, bgcolor: '#ede9fe', color: '#6366f1' }} />
            <Typography sx={{ flex: 1, fontSize: 13, color: '#374151' }}>{item.text}</Typography>
            <Button size="small" color="error" onClick={() => handleDelete(item._id)} sx={{ minWidth: 0, fontSize: 11 }}>✕</Button>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function HelpCenterTab() {
  const ACCENT = "#0ea5e9";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [topic, setTopic] = useState("");
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState("");
  const botId = localStorage.getItem("selectedBotId");

  useEffect(() => {
    if (!botId) { setLoading(false); return; }
    import("../api/botApi").then(({ getBotById }) => {
      getBotById(botId).then(data => {
        const hc = data?.botSettings?.helpCenter;
        if (Array.isArray(hc)) {
          setItems(hc.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
        }
      }).catch(() => {}).finally(() => setLoading(false));
    });
  }, [botId]);

  const update = (idx, patch) => setItems(list => list.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const remove = (idx) => setItems(list => list.filter((_, i) => i !== idx));
  const move = (idx, dir) => setItems(list => {
    const next = list.slice();
    const target = idx + dir;
    if (target < 0 || target >= next.length) return list;
    [next[idx], next[target]] = [next[target], next[idx]];
    return next;
  });
  const add = () => setItems(list => [...list, { icon: "📄", title: "", preview: "", body: "", enabled: true }]);

  const handleGenerate = async () => {
    if (!botId) return toast.error("Select a bot first");
    setGenerating(true);
    setGenError("");
    try {
      const { generateHelpCenter } = await import("../api/botApi");
      const res = await generateHelpCenter(botId, { topic: topic.trim(), count: topic.trim() ? 1 : 5 });
      const generated = (res?.articles || []).map(a => ({
        icon: a.icon || "📄",
        title: a.title || "",
        preview: a.preview || "",
        body: a.body || "",
        enabled: true,
      }));
      if (generated.length === 0) { setGenError("AI returned nothing. Try again."); return; }
      setItems(list => [...list, ...generated]);
      setTopic("");
    } catch (e) {
      setGenError(typeof e === "string" ? e : "Failed to generate. Try again.");
    } finally { setGenerating(false); }
  };

  const handleSave = async () => {
    if (!botId) return toast.error("Select a bot first");
    setSaving(true);
    try {
      const helpCenter = items
        .filter(it => (it.title || "").trim())
        .map((it, i) => ({
          icon: (it.icon || "📄").trim() || "📄",
          title: it.title.trim(),
          preview: (it.preview || "").trim(),
          body: (it.body || "").trim(),
          order: i,
          enabled: it.enabled !== false,
        }));
      const { updateBot } = await import("../api/botApi");
      await updateBot(botId, { botSettings: { helpCenter } });
      setItems(helpCenter);
      toast.success('Help Center saved!');
    } catch (e) { toast.error(typeof e === "string" ? e : "Failed to save"); }
    finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={24} /></Box>;

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography sx={{
          fontSize: 22, fontWeight: 800, color: "#111827", letterSpacing: "-0.4px",
          background: "linear-gradient(135deg, #1f2937, #0ea5e9)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
        }}>
          Help Center
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: "#6b7280", mt: 0.5 }}>
          Articles shown when visitors tap “Help” in your chatbot. Tailor them to your business — the AI bot also uses them to answer questions.
        </Typography>
      </Box>

      {/* AI generator */}
      <Box sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: "1px solid #ddd6fe",
        background: "linear-gradient(135deg, rgba(139,92,246,0.06), rgba(14,165,233,0.05))" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <AutoAwesomeIcon sx={{ color: "#8b5cf6", fontSize: 20 }} />
          <Typography sx={{ fontWeight: 700, fontSize: 14.5, color: "#111827" }}>Generate with AI</Typography>
        </Box>
        <Typography sx={{ fontSize: 12.5, color: "#6b7280", mb: 1.5 }}>
          Leave the topic empty to auto-create 5 common articles for your business, or enter a topic for one focused article. Generated articles are added below — review, edit, then Save.
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <TextField size="small" placeholder="Optional topic, e.g. “SEO services” or “refund policy”"
            value={topic} onChange={e => setTopic(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !generating && handleGenerate()}
            sx={{ flex: 1, minWidth: 240, "& .MuiOutlinedInput-root": { borderRadius: "11px", bgcolor: "#fff" } }} />
          <Button variant="contained" onClick={handleGenerate} disabled={generating}
            startIcon={generating ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <AutoAwesomeIcon sx={{ fontSize: 18 }} />}
            sx={{ borderRadius: "11px", textTransform: "none", fontWeight: 700, px: 3, whiteSpace: "nowrap",
              background: "linear-gradient(135deg, #8b5cf6, #6366f1)",
              boxShadow: "0 4px 16px rgba(139,92,246,0.35)",
              "&:hover": { background: "linear-gradient(135deg, #7c3aed, #4f46e5)" },
              "&.Mui-disabled": { background: "#d1d5db", boxShadow: "none", color: "#fff" } }}>
            {generating ? "Generating..." : "Generate"}
          </Button>
        </Box>
        {genError && <Typography sx={{ fontSize: 12, color: "#dc2626", mt: 1 }}>{genError}</Typography>}
      </Box>

      {items.length === 0 && (
        <Box sx={{ textAlign: "center", py: 5, px: 3, bgcolor: "#f8fafc", borderRadius: 3, border: "1px dashed #cbd5e1", mb: 2.5 }}>
          <HelpCenterIcon sx={{ fontSize: 40, color: "#94a3b8", mb: 1 }} />
          <Typography sx={{ fontSize: 14, color: "#64748b", mb: 0.5, fontWeight: 600 }}>No help articles yet</Typography>
          <Typography sx={{ fontSize: 12.5, color: "#9ca3af" }}>
            Until you add articles, visitors see a few generic defaults. Add your own to customize the Help Center.
          </Typography>
        </Box>
      )}

      {items.map((it, idx) => (
        <SectionCard key={idx} accent={ACCENT}
          title={it.title?.trim() || `Article ${idx + 1}`}
          desc={it.enabled === false ? "Hidden from visitors" : "Visible to visitors"}>
          <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
            <TextField size="small" label="Icon" value={it.icon || ""} onChange={e => update(idx, { icon: e.target.value })}
              sx={{ width: 80, "& .MuiOutlinedInput-root": { borderRadius: "11px", bgcolor: "#f9fafb" } }} />
            <TextField size="small" label="Title" value={it.title || ""} onChange={e => update(idx, { title: e.target.value })}
              sx={{ flex: 1, minWidth: 200, "& .MuiOutlinedInput-root": { borderRadius: "11px", bgcolor: "#f9fafb" } }} />
          </Box>
          <TextField size="small" label="Short preview" fullWidth value={it.preview || ""} onChange={e => update(idx, { preview: e.target.value })}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "11px", bgcolor: "#f9fafb" } }} />
          <TextField size="small" label="Full answer (shown when clicked + used by AI)" fullWidth multiline minRows={3}
            value={it.body || ""} onChange={e => update(idx, { body: e.target.value })}
            sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: "11px", bgcolor: "#f9fafb" } }} />
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "#374151", mr: 0.5 }}>Visible</Typography>
              <Switch size="small" checked={it.enabled !== false} onChange={e => update(idx, { enabled: e.target.checked })} />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Button size="small" onClick={() => move(idx, -1)} disabled={idx === 0}
                sx={{ minWidth: 0, color: "#64748b" }}><ArrowUpwardIcon fontSize="small" /></Button>
              <Button size="small" onClick={() => move(idx, 1)} disabled={idx === items.length - 1}
                sx={{ minWidth: 0, color: "#64748b" }}><ArrowDownwardIcon fontSize="small" /></Button>
              <Button size="small" color="error" onClick={() => remove(idx)} startIcon={<DeleteOutlineIcon fontSize="small" />}
                sx={{ textTransform: "none", fontWeight: 600 }}>Delete</Button>
            </Box>
          </Box>
        </SectionCard>
      ))}

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1, flexWrap: "wrap" }}>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={add}
          sx={{ borderRadius: "11px", textTransform: "none", fontWeight: 700, borderColor: ACCENT, color: ACCENT,
            "&:hover": { borderColor: ACCENT, bgcolor: `${ACCENT}0d` } }}>
          Add Article
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? undefined : <SaveIcon sx={{ fontSize: 18 }} />}
          sx={{
            borderRadius: "11px", textTransform: "none", fontWeight: 700, fontSize: 14, px: 4.5, py: 1.2,
            background: "linear-gradient(135deg, #0ea5e9, #0284c7)",
            boxShadow: "0 4px 16px rgba(14,165,233,0.35)",
            "&:hover": { background: "linear-gradient(135deg, #0284c7, #0369a1)", transform: "translateY(-1px)" },
            "&.Mui-disabled": { background: "#d1d5db", boxShadow: "none" },
            transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
          }}>
          {saving ? "Saving..." : "Save Help Center"}
        </Button>
      </Box>
    </Box>
  );
}

function ContactCollectionTab() {
  const ACCENT = "#ec4899";
  const botId = localStorage.getItem('selectedBotId');
  const [config, setConfig] = useState({
    enabled: false,
    autoCapture: { name: true, email: true, phone: true },
    saveToConversation: true,
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!botId) { setLoading(false); return; }
    import("../api/conversationApi").then(({ fetchContactCollectionConfig }) => {
      fetchContactCollectionConfig(botId).then(res => {
        if (res?.config) setConfig(res.config);
      }).catch(() => {}).finally(() => setLoading(false));
    });
  }, [botId]);

  const handleSave = async () => {
    if (!botId) return toast.error("Select a bot first");
    setSaving(true);
    try {
      const { saveContactCollectionConfig } = await import("../api/conversationApi");
      await saveContactCollectionConfig(botId, config);
      toast.success('Contact collection settings saved!');
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Failed to save';
      toast.error(msg);
      console.error('[ContactCollection] Save error:', e?.response?.data || e);
    } finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={24} /></Box>;

  return (
    <Box sx={{ p: 2, maxWidth: 600 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#374151', mb: 0.5 }}>Contact Collection</Typography>
      <Typography sx={{ fontSize: 12, color: '#9ca3af', mb: 3 }}>
        Auto-capture names, emails, and phone numbers from visitor messages.
      </Typography>

      {/* Enable toggle */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2, borderRadius: 3, border: '1px solid #f0f0f0' }}>
        <FormControlLabel
          control={<Switch checked={config.enabled} onChange={e => setConfig(p => ({ ...p, enabled: e.target.checked }))} sx={{ '& .MuiSwitch-thumb': { bgcolor: config.enabled ? ACCENT : '#bdbdbd' }, '& .MuiSwitch-track': { bgcolor: config.enabled ? `${ACCENT}40` : '#e0e0e0' } }} />}
          label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Enable Contact Collection</Typography>}
        />
        <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.5, ml: 5 }}>
          When enabled, visitor contact info is automatically detected and saved.
        </Typography>
      </Paper>

      {config.enabled && (
        <>
          {/* Auto-capture fields */}
          <Paper elevation={0} sx={{ p: 2.5, mb: 2, borderRadius: 3, border: '1px solid #f0f0f0' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#374151', mb: 1.5 }}>Auto-Capture Settings</Typography>
            <FormControlLabel
              control={<Switch checked={config.autoCapture.email} onChange={e => setConfig(p => ({ ...p, autoCapture: { ...p.autoCapture, email: e.target.checked } }))} size="small" />}
              label={<Typography sx={{ fontSize: 13 }}>Auto-capture email addresses</Typography>}
              sx={{ mb: 0.5, display: 'flex' }}
            />
            <Typography sx={{ fontSize: 11, color: '#9ca3af', ml: 5, mb: 1.5 }}>
              Detects patterns like "my email is user@example.com" or "user@domain.com"
            </Typography>
            <FormControlLabel
              control={<Switch checked={config.autoCapture.phone} onChange={e => setConfig(p => ({ ...p, autoCapture: { ...p.autoCapture, phone: e.target.checked } }))} size="small" />}
              label={<Typography sx={{ fontSize: 13 }}>Auto-capture phone numbers</Typography>}
              sx={{ mb: 0.5, display: 'flex' }}
            />
            <Typography sx={{ fontSize: 11, color: '#9ca3af', ml: 5, mb: 1.5 }}>
              Detects patterns like "+91 98765 43210" or "9876543210"
            </Typography>
            <FormControlLabel
              control={<Switch checked={config.autoCapture.name} onChange={e => setConfig(p => ({ ...p, autoCapture: { ...p.autoCapture, name: e.target.checked } }))} size="small" />}
              label={<Typography sx={{ fontSize: 13 }}>Auto-capture names</Typography>}
              sx={{ mb: 0.5, display: 'flex' }}
            />
            <Typography sx={{ fontSize: 11, color: '#9ca3af', ml: 5 }}>
              Detects single-word names like "Aman" or "Priya" from visitor messages.
            </Typography>
          </Paper>

          {/* Save location */}
          <Paper elevation={0} sx={{ p: 2.5, mb: 2, borderRadius: 3, border: '1px solid #f0f0f0' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#374151', mb: 1.5 }}>Storage</Typography>
            <FormControlLabel
              control={<Switch checked={config.saveToConversation} onChange={e => setConfig(p => ({ ...p, saveToConversation: e.target.checked }))} size="small" />}
              label={<Typography sx={{ fontSize: 13 }}>Save to conversation record</Typography>}
              sx={{ mb: 0.5, display: 'flex' }}
            />
            <Typography sx={{ fontSize: 11, color: '#9ca3af', ml: 5 }}>
              Saves captured contacts directly to the conversation for lead scoring.
            </Typography>
          </Paper>
        </>
      )}

      <Button variant="contained" onClick={handleSave} disabled={saving}
        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 4,
          background: `linear-gradient(135deg, ${ACCENT}, #db2777)`, color: '#fff',
          '&:hover': { filter: 'brightness(1.05)' },
          '&.Mui-disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' } }}>
        {saving ? "Saving..." : "Save Settings"}
      </Button>
    </Box>
  );
}

// ─── Instagram DM Tab ───
const IG_ACCENT = "#E4405F";
function InstagramTab() {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [account, setAccount] = useState(null);
  const [settings, setSettings] = useState({ autoDmEnabled: false, autoDmMessage: "", aiEnabled: false, aiBotId: null });
  const [manualToken, setManualToken] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [pageId, setPageId] = useState("");
  const [linkingPage, setLinkingPage] = useState(false);
  const [needsPageId, setNeedsPageId] = useState(false);
  const [bots, setBots] = useState([]);
  const botId = localStorage.getItem("selectedBotId");

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("error") === "page_id_required") {
      setNeedsPageId(true);
      q.delete("error");
      window.history.replaceState({}, "", `${window.location.pathname}?${q}`);
    }
  }, []);

  const load = async () => {
    try {
      const { getInstagramSettings } = await import("../api/instagramApi");
      const res = await getInstagramSettings();
      if (res.connected) {
        setConnected(true);
        setAccount(res.account);
        setSettings(res.settings);
      }
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!settings.aiEnabled) return;
    import("../api/botApi").then(({ getBots }) => {
      getBots().then(res => {
        if (Array.isArray(res)) setBots(res);
        else if (res?.bots) setBots(res.bots);
      }).catch(() => {});
    });
  }, [settings.aiEnabled]);

  const handleConnect = async () => {
    try {
      const { getAuthUrl } = await import("../api/instagramApi");
      const res = await getAuthUrl();
      if (res.url) window.location.href = res.url;
    } catch (e) { toast.error("Failed to get auth URL"); }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect Instagram? Auto DM and AI replies will stop.")) return;
    try {
      const { disconnectInstagram } = await import("../api/instagramApi");
      await disconnectInstagram();
      setConnected(false);
      setAccount(null);
      setSettings({ autoDmEnabled: false, autoDmMessage: "", aiEnabled: false, aiBotId: null });
      toast.success("Instagram disconnected");
    } catch (e) { toast.error("Failed to disconnect"); }
  };

  const handleManualConnect = async () => {
    if (!manualToken.trim()) return toast.error("Paste the Page Access Token first");
    setVerifying(true);
    try {
      const { verifyInstagramToken } = await import("../api/instagramApi");
      const res = await verifyInstagramToken(manualToken.trim());
      if (res.ok) {
        setConnected(true);
        setAccount(res.account);
        toast.success(`Connected as @${res.account.igUsername}`);
      } else {
        toast.error(res.error || "Verification failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || "Failed to verify token");
    } finally { setVerifying(false); }
  };

  const handleLinkPage = async () => {
    if (!pageId.trim()) return toast.error("Enter your Facebook Page name or ID");
    setLinkingPage(true);
    try {
      const { linkInstagramPage } = await import("../api/instagramApi");
      const res = await linkInstagramPage(pageId.trim());
      if (res.ok) {
        setConnected(true);
        setAccount(res.account);
        setNeedsPageId(false);
        toast.success(`Connected as @${res.account.igUsername}`);
      } else {
        toast.error(res.error || "Failed to link page");
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || "Failed to link page");
    } finally { setLinkingPage(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { saveInstagramSettings } = await import("../api/instagramApi");
      const res = await saveInstagramSettings(settings);
      setSettings(res.settings);
      toast.success("Instagram settings saved!");
    } catch (e) { toast.error(e?.response?.data?.error || e.message || "Failed to save settings"); }
    finally { setSaving(false); }
  };

  const update = (key, val) => setSettings(s => ({ ...s, [key]: val }));

  if (loading) return <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={24} /></Box>;

  return (
    <Box sx={{ p: 2, maxWidth: 700 }}>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{
          fontSize: 16, fontWeight: 700, color: "#111827", display: "flex", alignItems: "center", gap: 1,
        }}>
          <InstagramIcon sx={{ color: IG_ACCENT }} /> Instagram DM
        </Typography>
        <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.5 }}>
          Connect your Instagram Business/Creator account to auto-reply to DMs with AI.
        </Typography>
      </Box>

      {!connected ? (
        <>
        {needsPageId ? (
          <SectionCard accent="#6366f1" icon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
            title="Link Your Facebook Page"
            desc="We found your Instagram login but couldn't auto-detect your Facebook Page. Enter your Page ID to complete setup.">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Typography sx={{ fontSize: 12, color: "#6b7280", lineHeight: 1.6 }}>
                Apne Facebook Page ka naam ya ID daalein. Jaise: <code>taltokam</code> ya <code>123456789012345</code>
              </Typography>
              <TextField size="small" fullWidth
                placeholder="Enter your Facebook Page name or ID"
                value={pageId} onChange={e => setPageId(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#f9fafb", fontFamily: "monospace", fontSize: 12 } }} />
              <Button variant="contained" onClick={handleLinkPage} disabled={linkingPage || !pageId.trim()}
                startIcon={linkingPage ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null}
                sx={{
                  borderRadius: "11px", textTransform: "none", fontWeight: 700, px: 3, alignSelf: "flex-start",
                  background: "linear-gradient(135deg, #6366f1, #4f46e5)",
                  "&:hover": { background: "linear-gradient(135deg, #4f46e5, #4338ca)" },
                  "&.Mui-disabled": { background: "#d1d5db" },
                }}>
                {linkingPage ? "Connecting..." : "Link Page"}
              </Button>
            </Box>
          </SectionCard>
        ) : (
        <SectionCard accent={IG_ACCENT} icon={<InstagramIcon sx={{ fontSize: 16 }} />}
          title="Connect Instagram"
          desc="Link your Instagram Business or Creator account to enable DM automation.">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
            <Typography sx={{ fontSize: 12.5, color: "#6b7280", lineHeight: 1.6 }}>
              Requires a Business or Creator Instagram account. You'll be prompted to log into Facebook and grant permissions.
            </Typography>
            <Button variant="contained" onClick={handleConnect}
              startIcon={<InstagramIcon />}
              sx={{
                borderRadius: "11px", textTransform: "none", fontWeight: 700, px: 4,
                background: `linear-gradient(135deg, #E4405F, #C13584)`,
                boxShadow: `0 4px 16px rgba(228,64,95,0.35)`,
                "&:hover": { background: "linear-gradient(135deg, #C13584, #833AB4)" },
              }}>
              Connect Instagram
            </Button>
          </Box>
        </SectionCard>
        )}

        <SectionCard accent="#6b7280" icon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
          title="Or Connect Manually"
          desc="Get a Page Access Token from Graph API Explorer and paste it here.">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography sx={{ fontSize: 12, color: "#6b7280", lineHeight: 1.6 }}>
              1. Open <b>Graph API Explorer</b> → <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noreferrer">developers.facebook.com/tools/explorer/</a>
              <br />2. Select App: your Meta App
              <br />3. <b>User or Page</b> → select <b>Page Token</b> → choose your Facebook Page
              <br />4. Add Permissions: <code>pages_manage_metadata, pages_read_engagement, pages_manage_pages, instagram_basic, instagram_manage_messages</code>
              <br />5. <b>Generate Access Token</b> → copy the token
            </Typography>
            <TextField size="small" fullWidth multiline minRows={2}
              placeholder="Paste Page Access Token here..."
              value={manualToken} onChange={e => setManualToken(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#f9fafb", fontFamily: "monospace", fontSize: 12 } }} />
            <Button variant="contained" onClick={handleManualConnect} disabled={verifying || !manualToken.trim()}
              startIcon={verifying ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null}
              sx={{
                borderRadius: "11px", textTransform: "none", fontWeight: 700, px: 3, alignSelf: "flex-start",
                background: "linear-gradient(135deg, #6b7280, #4b5563)",
                "&:hover": { background: "linear-gradient(135deg, #4b5563, #374151)" },
                "&.Mui-disabled": { background: "#d1d5db" },
              }}>
              {verifying ? "Verifying..." : "Verify & Connect"}
            </Button>
          </Box>
        </SectionCard>
        </>
      ) : (
        <>
          {/* Connected account info */}
          <SectionCard accent={IG_ACCENT} icon={<InstagramIcon sx={{ fontSize: 16 }} />}
            title={account?.igUsername || "Instagram Connected"}
            desc={account?.fbPageName ? `Facebook Page: ${account.fbPageName}` : "Instagram Business account connected"}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Chip label="Connected" size="small" sx={{
                bgcolor: "#10b98115", color: "#10b981", fontWeight: 700, fontSize: 12,
              }} />
              <Button size="small" onClick={handleConnect}
                sx={{ textTransform: "none", fontSize: 12, color: "#6366f1" }}>
                Re-connect
              </Button>
              <Button size="small" onClick={handleDisconnect}
                sx={{ textTransform: "none", fontSize: 12, color: "#ef4444" }}>
                Disconnect
              </Button>
            </Box>
          </SectionCard>

          {/* Auto DM */}
          <SectionCard accent="#C13584" icon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
            title="Auto DM"
            desc="Send an automatic welcome message when someone DMs your Instagram for the first time.">
            <FormControlLabel
              control={<Switch checked={settings.autoDmEnabled} onChange={e => update("autoDmEnabled", e.target.checked)}
                sx={{ "& .MuiSwitch-thumb": { bgcolor: settings.autoDmEnabled ? IG_ACCENT : "#bdbdbd" }, "& .MuiSwitch-track": { bgcolor: settings.autoDmEnabled ? `${IG_ACCENT}40` : "#e0e0e0" } }} />}
              label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Enable Auto DM</Typography>}
              sx={{ mb: 1.5 }}
            />
            {settings.autoDmEnabled && (
              <TextField multiline minRows={3} fullWidth size="small"
                placeholder="Hi {{name}}! Thanks for reaching out to {{brand}}. How can we help you today?"
                value={settings.autoDmMessage}
                onChange={e => update("autoDmMessage", e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#f9fafb" } }}
              />
            )}
            <Typography sx={{ fontSize: 11, color: "#9ca3af", mt: 1 }}>
              Use {'{name}'} for sender name and {'{brand}'} for your brand name.
            </Typography>
          </SectionCard>

          {/* AI Replies */}
          <SectionCard accent="#833AB4" icon={<SmartToyIcon sx={{ fontSize: 16 }} />}
            title="AI Reply"
            desc="Let your AI bot respond to Instagram DMs automatically.">
            <FormControlLabel
              control={<Switch checked={settings.aiEnabled} onChange={e => update("aiEnabled", e.target.checked)}
                sx={{ "& .MuiSwitch-thumb": { bgcolor: settings.aiEnabled ? "#833AB4" : "#bdbdbd" }, "& .MuiSwitch-track": { bgcolor: settings.aiEnabled ? `${"#833AB4"}40` : "#e0e0e0" } }} />}
              label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Enable AI Replies</Typography>}
              sx={{ mb: 1.5 }}
            />
            {settings.aiEnabled && (
              <FormControl size="small" fullWidth>
                <InputLabel>AI Bot</InputLabel>
                <Select value={settings.aiBotId || ""} label="AI Bot"
                  onChange={e => update("aiBotId", e.target.value || null)}
                  sx={{ borderRadius: 2, bgcolor: "#f9fafb" }}>
                  {bots.map(b => (
                    <MenuItem key={b._id} value={b._id}>{b.name}</MenuItem>
                  ))}
                  {bots.length === 0 && <MenuItem value="">No bots found. Create one in AI Bot settings.</MenuItem>}
                </Select>
              </FormControl>
            )}
            <Box sx={{ mt: 2, p: 1.5, bgcolor: "#fef3c7", borderRadius: 2, border: "1px solid #fde68a" }}>
              <Typography sx={{ fontSize: 11.5, color: "#92400e" }}>
                ⚠️ Instagram conversations are stored in your Leads page. Messages and AI replies are recorded.
              </Typography>
            </Box>
          </SectionCard>

          <Button variant="contained" onClick={handleSave} disabled={saving}
            startIcon={saving ? undefined : <SaveIcon />}
            sx={{
              borderRadius: "11px", textTransform: "none", fontWeight: 700, px: 4, py: 1.2,
              background: `linear-gradient(135deg, ${IG_ACCENT}, #C13584)`,
              boxShadow: `0 4px 16px rgba(228,64,95,0.35)`,
              "&:hover": { background: "linear-gradient(135deg, #C13584, #833AB4)" },
              "&.Mui-disabled": { background: "#d1d5db", boxShadow: "none" },
            }}>
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </>
      )}
    </Box>
  );
}

// ─── Email Automation Tab ───
const ACCENT_GREEN = "#10b981";
function EmailAutomationTab() {
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", triggerOn: "lead_capture", steps: [{ order: 1, delayHours: 0, subject: "", body: "" }] });
  const botId = localStorage.getItem("selectedBotId");

  const load = async () => {
    if (!botId) { setLoading(false); return; }
    try {
      const { fetchSequences } = await import("../api/emailApi");
      const res = await fetchSequences(botId);
      if (res?.ok) setSequences(res.sequences);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [botId]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", description: "", triggerOn: "lead_capture", steps: [{ order: 1, delayHours: 0, subject: "", body: "" }] });
    setDialogOpen(true);
  };

  const openEdit = (seq) => {
    setEditing(seq);
    setForm({ name: seq.name, description: seq.description || "", triggerOn: seq.triggerOn || "lead_capture", steps: seq.steps.length > 0 ? seq.steps : [{ order: 1, delayHours: 0, subject: "", body: "" }] });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name) return toast.error("Name is required");
    if (!botId) return toast.error("Select a bot first");
    try {
      const { createSequence, updateSequence } = await import("../api/emailApi");
      if (editing) {
        await updateSequence(editing._id, form);
        toast.success("Sequence updated!");
      } else {
        await createSequence({ ...form, botId });
        toast.success("Sequence created!");
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e?.message || "Failed to save"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this sequence? Active campaigns using it will be cancelled.")) return;
    try {
      const { deleteSequence } = await import("../api/emailApi");
      await deleteSequence(id);
      toast.success("Sequence deleted");
      load();
    } catch (e) { toast.error(e?.message || "Failed to delete"); }
  };

  const addStep = () => {
    setForm(f => ({ ...f, steps: [...f.steps, { order: f.steps.length + 1, delayHours: 0, subject: "", body: "" }] }));
  };

  const removeStep = (idx) => {
    setForm(f => ({ ...f, steps: f.steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 })) }));
  };

  const updateStep = (idx, key, val) => {
    setForm(f => ({ ...f, steps: f.steps.map((s, i) => i === idx ? { ...s, [key]: val } : s) }));
  };

  if (loading) return <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={24} /></Box>;

  return (
    <Box sx={{ p: 2, maxWidth: 800 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#374151' }}>Email Sequences</Typography>
          <Typography sx={{ fontSize: 12, color: '#9ca3af' }}>
            Create automated email sequences that trigger when a lead provides their email.
          </Typography>
        </Box>
        <Button variant="contained" size="small" onClick={openCreate}
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, bgcolor: ACCENT_GREEN, "&:hover": { bgcolor: "#059669" } }}>
          + New Sequence
        </Button>
      </Box>

      {sequences.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography sx={{ color: "#9ca3af", mb: 1 }}>No sequences yet.</Typography>
          <Typography sx={{ color: "#9ca3af", fontSize: 12 }}>Create your first email sequence to start nurturing leads automatically.</Typography>
        </Box>
      ) : (
        sequences.map(seq => (
          <Paper key={seq._id} elevation={0} sx={{ p: 2, mb: 1.5, borderRadius: 3, border: "1px solid #f0f0f0" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{seq.name}</Typography>
                <Typography sx={{ fontSize: 11, color: "#6b7280" }}>{seq.description}</Typography>
                <Typography sx={{ fontSize: 11, color: "#9ca3af", mt: 0.5 }}>
                  {seq.steps.length} step{seq.steps.length !== 1 ? "s" : ""} · trigger: {seq.triggerOn === "lead_capture" ? "Lead Capture" : "Manual"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Chip label={seq.status} size="small" sx={{
                  bgcolor: seq.status === "active" ? "#10b98115" : "#6b728015",
                  color: seq.status === "active" ? "#10b981" : "#6b7280",
                  fontWeight: 600, fontSize: 11,
                }} />
                <Button size="small" onClick={() => openEdit(seq)} sx={{ textTransform: "none", fontSize: 12 }}>Edit</Button>
                <Button size="small" onClick={() => handleDelete(seq._id)} sx={{ textTransform: "none", fontSize: 12, color: "#ef4444" }}>Delete</Button>
              </Box>
            </Box>
            {seq.steps.map((step, i) => (
              <Box key={i} sx={{ display: "flex", gap: 1, mt: 1, ml: 2, alignItems: "center" }}>
                <Typography sx={{ fontSize: 11, color: "#6b7280", minWidth: 60 }}>Step {i + 1}:</Typography>
                <Typography sx={{ fontSize: 12, color: "#374151" }}>
                  {step.delayHours === 0 ? "Immediately" : `+${step.delayHours}h`}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#374151", fontWeight: 500 }}>— {step.subject || "(no subject)"}</Typography>
              </Box>
            ))}
          </Paper>
        ))
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? "Edit Sequence" : "Create Sequence"}</DialogTitle>
        <DialogContent>
          <TextField label="Name" size="small" fullWidth sx={{ mt: 1, mb: 1.5 }}
            value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          <TextField label="Description" size="small" fullWidth sx={{ mb: 1.5 }}
            value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          <FormControl size="small" fullWidth sx={{ mb: 2 }}>
            <InputLabel>Trigger On</InputLabel>
            <Select value={form.triggerOn} label="Trigger On" onChange={e => setForm(f => ({ ...f, triggerOn: e.target.value }))}>
              <MenuItem value="lead_capture">Lead Capture (auto-start when email captured)</MenuItem>
              <MenuItem value="manual">Manual (start from Campaigns page)</MenuItem>
            </Select>
          </FormControl>

          <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>Email Steps</Typography>
          {form.steps.map((step, idx) => (
            <Paper key={idx} elevation={0} sx={{ p: 2, mb: 1.5, border: "1px solid #e5e7eb", borderRadius: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Step {idx + 1}</Typography>
                {form.steps.length > 1 && (
                  <Button size="small" onClick={() => removeStep(idx)} sx={{ color: "#ef4444", fontSize: 11, textTransform: "none" }}>Remove</Button>
                )}
              </Box>
              <TextField label="Delay (hours)" type="number" size="small" fullWidth sx={{ mb: 1 }}
                value={step.delayHours} onChange={e => updateStep(idx, "delayHours", parseInt(e.target.value) || 0)} />
              <TextField label="Email Subject (use {name}, {email})" size="small" fullWidth sx={{ mb: 1 }}
                value={step.subject} onChange={e => updateStep(idx, "subject", e.target.value)} />
              <TextField label="Email Body (HTML, use {name}, {email})" size="small" fullWidth multiline rows={4}
                value={step.body} onChange={e => updateStep(idx, "body", e.target.value)} />
            </Paper>
          ))}
          <Button onClick={addStep} size="small" sx={{ textTransform: "none", color: ACCENT_GREEN }}>
            + Add Step
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" sx={{ textTransform: "none", bgcolor: ACCENT_GREEN }}>
            {editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
